// src/api/uploadFile.ts
import { UploadError } from './uploadError'

export type CompressionType =
  'banner_compression' | 'gif' | 'without_compression' | 'user_compression' | 'video' | 'rive'

interface UploadProgress {
  loaded: number
  total: number
  percent: number
}

export async function uploadFileWithProgress(params: {
  file: File
  compressionType: CompressionType
  accessToken: string
  onProgress?: (p: UploadProgress) => void
  signal?: AbortSignal
}): Promise<string> {
  const { file, compressionType, accessToken, onProgress, signal } = params
  const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1'
  const url = `${API_URL}/utils/upload-file?compression_type=${compressionType}`

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.setRequestHeader('Authorization', accessToken)

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && onProgress) {
        onProgress({ loaded: ev.loaded, total: ev.total, percent: (ev.loaded / ev.total) * 100 })
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const cdnUrl = JSON.parse(xhr.responseText) as string
          resolve(cdnUrl)
        } catch {
          resolve(xhr.responseText)
        }
      } else {
        reject(new UploadError(xhr.status, xhr.responseText))
      }
    }

    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.onabort = () => reject(new Error('Upload aborted'))

    if (signal) signal.addEventListener('abort', () => xhr.abort())

    const fd = new FormData()
    fd.append('file', file)
    xhr.send(fd)
  })
}
